import React from 'react';
import { ListenButton } from '../common/AudioGuide';
import HelpWithVideoTour from '../Help/HelpWithVideoTour';
import TimeTrackerSafetyNets from './TimeTrackerSafetyNets';

export const TIME_TRACKER_ADMIN_HELP_GUIDE_ID = 'time-tracker-admin-help';
const ACCENT = '#0f766e';

const SectionLabel = ({ children }) => (
  <h4 style={{
    margin: '0 0 10px', fontSize: 12, fontWeight: 800, color: '#334155',
    textTransform: 'uppercase', letterSpacing: 0.6,
  }}>
    {children}
  </h4>
);

const Card = ({ icon, title, body, tone = 'slate' }) => {
  const tones = {
    teal: { bg: 'linear-gradient(180deg, #f0fdfa 0%, #fff 70%)', border: '#99f6e4' },
    blue: { bg: 'linear-gradient(180deg, #eff6ff 0%, #fff 70%)', border: '#bfdbfe' },
    amber: { bg: 'linear-gradient(180deg, #fffbeb 0%, #fff 70%)', border: '#fde68a' },
    rose: { bg: 'linear-gradient(180deg, #fff1f2 0%, #fff 70%)', border: '#fecdd3' },
    slate: { bg: '#fff', border: '#e2e8f0' },
  };
  const t = tones[tone] || tones.slate;
  return (
    <div style={{ background: t.bg, border: `1px solid ${t.border}`, borderRadius: 12, padding: '12px 14px' }}>
      <div style={{ fontSize: 20, marginBottom: 4 }}>{icon}</div>
      <div style={{ fontWeight: 800, fontSize: 13, color: '#0f172a' }}>{title}</div>
      <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.45, marginTop: 4 }}>{body}</div>
    </div>
  );
};

const TipRow = ({ letter, chip, color, title, body }) => (
  <div style={{
    display: 'flex', gap: 12, padding: '12px 14px',
    background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
  }}>
    <div style={{
      width: 34, height: 34, borderRadius: 8, background: chip, color,
      fontWeight: 800, fontSize: 13, display: 'flex', alignItems: 'center',
      justifyContent: 'center', flexShrink: 0,
    }}>{letter}</div>
    <div>
      <div style={{ fontWeight: 800, fontSize: 13, color: '#0f172a' }}>{title}</div>
      <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.45, marginTop: 2 }}>{body}</div>
    </div>
  </div>
);

/**
 * Time Tracker Admin help — clerks / controller / superadmin.
 * Employees screen still uses hiring-layoff-help; this guide points there.
 */
const TimeTrackerAdminHelpModal = ({ onClose }) => {
  if (!onClose) return null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 12000, padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 880,
        maxHeight: '92vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 24px 60px rgba(15,23,42,0.32)', overflow: 'hidden',
      }}>
        <div style={{
          padding: '16px 20px',
          background: `linear-gradient(135deg, ${ACCENT} 0%, #115e59 55%, #134e4a 100%)`,
          color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.2, opacity: 0.85, textTransform: 'uppercase' }}>
              Time Tracker Admin
            </div>
            <h3 style={{ margin: '2px 0 0', fontSize: 18, fontWeight: 800 }}>Codes, extras, lock, and payroll</h3>
            <div style={{ fontSize: 12, opacity: 0.9, marginTop: 3 }}>
              Cost clerk · controller · superadmin
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ListenButton compact guideId={TIME_TRACKER_ADMIN_HELP_GUIDE_ID} title="Time Tracker Admin Help" accent={ACCENT} dark />
            <button
              type="button"
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.18)', border: 'none', color: '#fff',
                borderRadius: 8, width: 34, height: 34, cursor: 'pointer', fontSize: 20, lineHeight: 1,
              }}
              aria-label="Close"
            >×</button>
          </div>
        </div>

        <div style={{ padding: '18px 20px 22px', overflowY: 'auto', flex: 1 }}>
          <HelpWithVideoTour
            guideId={TIME_TRACKER_ADMIN_HELP_GUIDE_ID}
            title="Time Tracker Admin Help"
            accent={ACCENT}
            interactiveVariant="admin"
            onStartInteractive={() => {
              onClose();
              window.dispatchEvent(new CustomEvent('tt:startInteractiveTour', { detail: { variant: 'admin' } }));
            }}
            extraTabs={[{ id: 'safety', label: 'Safety nets', content: <TimeTrackerSafetyNets /> }]}
          >
            <section style={{ marginBottom: 22 }}>
              <SectionLabel>1. Who Admin is for</SectionLabel>
              <p style={{ margin: 0, fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
                Cost clerks, the controller, and superadmin. The portal checks that on the button and again on every write.
                Everyone else is refused. Field people should use the teal field Help.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8, marginTop: 10 }}>
                <Card icon="👁️" title="Everyone’s hours" body="Accounting, including payroll and accounts payable, plus superadmin." tone="teal" />
                <Card icon="🧾" title="Audit" body="The same accounting group. Foremen do not see Audit." tone="blue" />
                <Card icon="⚙️" title="This Admin gear" body="Cost clerk, controller, superadmin only. Payroll can see hours and still not see these six cards." tone="amber" />
              </div>
            </section>

            <section style={{ marginBottom: 22 }}>
              <SectionLabel>2. The six cards</SectionLabel>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
                <Card icon="📋" title="Op Codes" body="Standard labour list per job and discipline." tone="teal" />
                <Card icon="👥" title="Employees" body="Same master as Accounting Hiring / Layoff. Thinner editor." tone="blue" />
                <Card icon="🔶" title="Extras" body="Change-order codes. Change Log is the source of extra#." tone="amber" />
                <Card icon="📄" title="Reports" body="Four PDF packs: Details, Master Payroll, OP Code, Contract." />
                <Card icon="🔒" title="Period Lock" body="Create and lock CA / US payroll weeks." tone="rose" />
                <Card icon="📤" title="Maestro Export" body="Nine-line payroll file for the week and project." tone="blue" />
              </div>
            </section>

            <section style={{ marginBottom: 22 }}>
              <SectionLabel>3. The rule: job plus discipline</SectionLabel>
              <div style={{
                padding: '14px 16px', borderRadius: 12,
                background: 'linear-gradient(180deg, #ecfdf5 0%, #fff 70%)',
                border: '1px solid #6ee7b7',
              }}>
                <p style={{ margin: 0, fontSize: 13, color: '#065f46', lineHeight: 1.5, fontWeight: 700 }}>
                  Admin → Op Codes and Admin → Extras always open a job, then a discipline.
                </p>
                <p style={{ margin: '8px 0 0', fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
                  A job is listed when it has a discipline on the project, or when the job number already ends in PIP, MWR, ELE, SHM, PFS, CIV, STR, TNK, or BMT.
                  Change Log writes the extra onto that same pair. Search the job number (30437), pick 30437PIP, then PIP.
                  That is the normal path, not a workaround.
                </p>
              </div>
            </section>

            <section style={{ marginBottom: 22 }}>
              <SectionLabel>4. Op Codes</SectionLabel>
              <p style={{ margin: 0, fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
                Pick the project-discipline, then add rows or import Excel. Codes must be numeric and at most five digits
                (zero-padded). Green dot = timesheet can see it. Gray hides it from new entries without deleting history.
                Empty field dropdown means this list was never saved for that pair.
              </p>
            </section>

            <section style={{ marginBottom: 22 }}>
              <SectionLabel>5. Extras from Change Log</SectionLabel>
              <p style={{ margin: 0, fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
                Saving a Change Log extra number writes the Time Tracker extra onto that job-discipline pair.
                Open Admin → Extras, search the job number, pick the job, then the discipline. Synced rows show an amber
                <strong> Change Log</strong> badge; code and description stay locked. Your job is the active toggle.
                Remove is a soft delete. Accounting restores from Audit. Extra ranges sit 60 000 above the labour range.
              </p>
            </section>

            <section style={{ marginBottom: 22 }}>
              <SectionLabel>6. Employees — three different counts</SectionLabel>
              <div style={{ display: 'grid', gap: 8 }}>
                <TipRow letter="1" chip="#ccfbf1" color={ACCENT} title="~1383 master" body="Accounting’s Hiring / Layoff roster: everyone ever coded (active + leave + terminated). Same table as this screen." />
                <TipRow letter="2" chip="#dbeafe" color="#2563eb" title="~227 Entra logins" body="People with a Laframboise email in Azure AD. This is the office login list. It is NOT “227 of the 1383.” Some Entra users have no four-digit employee code at all." />
                <TipRow letter="3" chip="#fef3c7" color="#d97706" title="Unmatched actives" body="Active craft on the master with no Entra employee ID or email match. The yellow banner used to show 200 because the review list was capped at 200 rows. The real count is the sync stale_count (about 800)." />
              </div>
              <p style={{
                margin: '10px 0 0', padding: '10px 12px', borderRadius: 8,
                background: '#fffbeb', border: '1px solid #fde68a',
                fontSize: 12, color: '#92400e', lineHeight: 1.45,
              }}>
                Draw a Venn diagram, do not subtract. Middle overlap = people with both a code and an email
                (last sync updated about 130). Left = field craft with no login. Right = office email with no
                payroll code. Timesheet picker is <strong>active-only</strong>. Full H R help: Hiring / Layoff ❓ Help.
              </p>
            </section>

            <section style={{ marginBottom: 22 }}>
              <SectionLabel>7. Placeholder codes</SectionLabel>
              <p style={{ margin: 0, fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
                Official placeholders: <strong>1111</strong> Need, <strong>9999</strong> New,
                <strong> 9993–9998</strong> Need-discipline-role. <strong>0000 is forbidden</strong>.
                Clear them before Maestro export so a Need row is not treated as a person.
              </p>
            </section>

            <section style={{ marginBottom: 22 }}>
              <SectionLabel>8. Discipline equals trade</SectionLabel>
              <p style={{ margin: 0, fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
                One field: <code>discipline_code</code>, matching the Coding Structure three-letter list
                (PIP MWR ELE SHM PFS CIV STR TNK BMT). The legacy <code>trade</code> column is not shown
                on this screen or on exports.
              </p>
            </section>

            <section style={{ marginBottom: 22 }}>
              <SectionLabel>9. Entra sync is email only</SectionLabel>
              <p style={{ margin: 0, fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
                Sync matches Employee ID or email, fills the email, and can create a portal user.
                It does not invent the roster and does not hide field craft who have no email.
                Sync Now refreshes the match. Review stale actives before you deactivate.
                Never type a fake email to silence the banner.
              </p>
            </section>

            <section style={{ marginBottom: 22 }}>
              <SectionLabel>10. Hiring / layoff path</SectionLabel>
              <p style={{ margin: 0, fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
                Tanya adds the four-digit code and name → Jordan creates the Entra email → daily sync links
                the portal user → that person can open Time Tracker. Leave or terminated revokes portal access
                and keeps history. Follow-up notes ride the digest until cleared. Audit lives on the Accounting screen.
              </p>
            </section>

            <section style={{ marginBottom: 22 }}>
              <SectionLabel>11. Crew management</SectionLabel>
              <p style={{ margin: 0, fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
                Create a crew with yourself as the head, add members, and assign projects. You can change that crew and transfer it to someone else.
                Accounting and superadmin can change any crew. Every change is written to Audit.
                Daily stays closed until you head a crew.
              </p>
            </section>

            <section style={{ marginBottom: 22 }}>
              <SectionLabel>12. Payroll period lock</SectionLabel>
              <p style={{ margin: 0, fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
                Create Canada and United States periods separately. Locking Canada freezes that week for every Canadian employee, on every job, not one project.
                The payroll group comes from the division name. A name that ends in (US) is the United States. A name that ends in (CA) is Canada.
                If the division does not say the country, or the person has no division, there is no group and the lock does not cover them.
                Field edits return locked. A manager can still correct the row. Unlock only for a true error, and do not lock while Friday hours are still being entered.
              </p>
            </section>

            <section style={{ marginBottom: 22 }}>
              <SectionLabel>13. Maestro payroll export</SectionLabel>
              <p style={{ margin: 0, fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
                Pick a Canada Sunday–Saturday week, a United States Monday–Sunday week, or a custom range, then the project.
                Each employee, job, code, and shift can become nine lines: regular, time and a half, double, height,
                travel hours, meals, room and board, travel dollars, and bus. A zero line is skipped.
              </p>
              <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
                <TipRow letter="A" chip="#fee2e2" color="#b91c1c" title="Drafts are in this file" body="The PDF leaves drafts out. This file does not. Submit or clear drafts before you export, or payroll will see hours the PDF does not." />
                <TipRow letter="B" chip="#ccfbf1" color={ACCENT} title="Left out on purpose" body="Removed rows stay out. The presentation job PRESO-TT-001 stays out. Do not export that job’s February–April 2026 window into a real payroll file." />
                <TipRow letter="C" chip="#dbeafe" color="#2563eb" title="The activity code can change" body="A normal job keeps the labour code you picked. A job that starts with T becomes activity 02001. A job number longer than 8 characters uses characters 10–14. Expense lines use activity 01015, except those long job numbers, which keep the derived code." />
              </div>
            </section>

            <section style={{ marginBottom: 22 }}>
              <SectionLabel>14. PDF reports</SectionLabel>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <Card icon="📄" title="Details" body="Every line. Audit trail." />
                <Card icon="📆" title="Master Payroll" body="One employee per week. CA vs US day order." tone="blue" />
                <Card icon="🔢" title="OP Code Summary" body="Contract → employee rollup." tone="teal" />
                <Card icon="📑" title="Contract Report" body="Contract → op code → employee day grid." tone="amber" />
              </div>
              <p style={{ margin: '10px 0 0', fontSize: 12, color: '#64748b' }}>
                Comments print as day-off notes. Empty report usually means drafts or the wrong week.
              </p>
            </section>

            <section style={{ marginBottom: 22 }}>
              <SectionLabel>15. Analytics before you lock</SectionLabel>
              <p style={{ margin: 0, fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
                Hours by trade, op code, and shift are a last look before lock and export.
                A spike on one code is often a mistyped extra or the wrong discipline. Fix it while the period is open.
              </p>
            </section>

            <section style={{ marginBottom: 22 }}>
              <SectionLabel>16. End-to-end payroll checklist</SectionLabel>
              <div style={{ display: 'grid', gap: 8 }}>
                {[
                  ['1', 'Load standard op codes for the job-discipline'],
                  ['2', 'Confirm Change Log extras landed (amber badge)'],
                  ['3', 'Confirm crews and heads'],
                  ['4', 'Keep the period open'],
                  ['5', 'Field submits. PDF is submitted only. Maestro still includes drafts'],
                  ['6', 'Lock the payroll group, not one job'],
                  ['7', 'Maestro export, then the PDF pack. Confirm no draft badges remain'],
                ].map(([n, t]) => (
                  <TipRow key={n} letter={n} chip="#ccfbf1" color={ACCENT} title={`Step ${n}`} body={t} />
                ))}
              </div>
            </section>

            <section style={{ marginBottom: 22 }}>
              <SectionLabel>17. Audit log</SectionLabel>
              <p style={{ margin: 0, fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
                The orange Audit button is Accounting and superadmin only. Daily and Weekly saves write the audit row in the same step as the hours,
                including room and board, meals, and travel. If that write fails, the hours are not kept.
                The log also records who removed, restored, locked, or synced codes, extras, crews, employees, and payroll periods.
                The log lives in the same database as the hours. Filter by type, action, date, or person. Restore a removed row from the same screen. Export CSV for a week close.
              </p>
            </section>

            <section style={{ marginBottom: 22 }}>
              <SectionLabel>18. Nothing is erased</SectionLabel>
              <p style={{ margin: 0, fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
                Time Tracker does not hard-delete. Timesheets, op codes, extras, crews, crew members, and payroll periods
                are marked removed and hidden. The row stays. Audit can bring it back. Employees are deactivated, never wiped.
              </p>
            </section>

            <section style={{ marginBottom: 22 }}>
              <SectionLabel>19. What keeps the tool standing</SectionLabel>
              <div style={{ display: 'grid', gap: 8 }}>
                <TipRow letter="1" chip="#ccfbf1" color={ACCENT} title="Azure database backups" body="Dev and production sit on Azure Database for PostgreSQL. Azure can restore the whole database to an earlier moment. A wrong row is an Audit restore. A damaged database is that Azure restore." />
                <TipRow letter="2" chip="#dbeafe" color="#2563eb" title="A copy outside Azure" body="A copy of the Time Tracker tables can be saved as files outside the database. Use that copy if the database itself is lost. The audit log is inside the database, so a database restore brings the log back with the hours. Git does not hold the hours." />
                <TipRow letter="3" chip="#fef3c7" color="#d97706" title="Period lock" body="After payroll posts, lock the week. Field edits return locked. Unlock is itself an audit event." />
                <TipRow letter="4" chip="#e2e8f0" color="#475569" title="Dev first, then prod" body="Code and migrations land on the dev database first. Git holds the code. The outside copy of the hours is a file backup, not something deploy creates on its own." />
              </div>
            </section>

            <section style={{ marginBottom: 8 }}>
              <SectionLabel>20. Admin troubleshooting</SectionLabel>
              <div style={{ display: 'grid', gap: 8 }}>
                <TipRow letter="A" chip="#ccfbf1" color={ACCENT} title="Empty dropdown" body="Open the job, then the discipline. Search the job number. Load Op Codes, or wait for Change Log sync. Check the green/gray status." />
                <TipRow letter="B" chip="#fee2e2" color="#b91c1c" title="Export 403" body="You are not in the admin role." />
                <TipRow letter="C" chip="#fef3c7" color="#d97706" title="Empty PDF" body="Filter, date range, or still-draft status." />
                <TipRow letter="D" chip="#e2e8f0" color="#475569" title="Entra mismatch" body="Look at the ~200 active unmatched, not the ~1383 total. Placeholders should be gone before Maestro." />
              </div>
            </section>
          </HelpWithVideoTour>
        </div>
      </div>
    </div>
  );
};

export default TimeTrackerAdminHelpModal;
